import { ChevronRight, X } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { Logo } from 'src/shared/components/icons/logos/logo'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { cn } from 'src/shared/utils/cn'

import { DirectionArrow } from '../../users/assets/direction-arrow'
import AuthModal from '../../users/components/marketplace/modals/auth'
import SidebarCardImg from '../assets/instant-match.png'

import InstantMatchButton from './instant-match-button'

type SidebarProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

const Sidebar = ({ isOpen, setIsOpen }: SidebarProps) => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)
  const [authModalFlow, setAuthModalFlow] = useState<'signup' | 'login'>('login')

  const onInstantMatch = () => {
    navigate(paths.marketplace.instantMatch.getHref())
  }

  const onFindAgent = () => {
    navigate(paths.marketplace.instantMatch.profession.getHref('real_estate_agent'))
  }

  const onGetMortgage = () => {
    navigate(paths.marketplace.instantMatch.profession.getHref('mortgage_loan_officer'))
  }

  const handleClientLoginClick = (signup = false) => {
    setIsOpen(false)
    setShowModal(true)
    setAuthModalFlow(signup ? 'signup' : 'login')
  }

  if (!isMobile) return null

  return (
    <>
      <div
        className={cn(
          'duration-[400ms] fixed inset-0 z-[51] bg-black/30 transition-opacity',
          isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={() => setIsOpen(false)}
      />
      <aside
        className={cn(
          'duration-[400ms] fixed left-0 top-0 z-[100] flex h-full w-[320px] flex-col gap-[24px] bg-white transition-transform',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        style={{ boxShadow: '2px 0 16px 0 rgba(0,0,0,0.10)' }}
      >
        <div className="flex flex-row items-center justify-between px-5 py-6 pb-2">
          <Logo className="h-[25px] w-[129px]" />
          <button onClick={() => setIsOpen(false)}>
            <X className="size-[25px] text-muted" />
          </button>
        </div>
        <div className="flex flex-col gap-1 px-5 pt-2">
          <button
            onClick={onFindAgent}
            className="flex w-full flex-row items-center justify-between rounded-lg py-2 text-lg font-semibold text-primary hover:bg-muted/10"
          >
            Find an agent <ChevronRight className="size-6" />
          </button>
          <button
            onClick={onGetMortgage}
            className="flex w-full flex-row items-center justify-between rounded-lg py-2 text-lg font-semibold text-primary hover:bg-muted/10"
          >
            Get a mortgage <ChevronRight className="size-6" />
          </button>
          <InstantMatchButton
            text="Instant Match Me!"
            className="my-3 h-[50px] w-full rounded-full text-[15px] font-bold tracking-normal"
          />

          <div className="mt-2 flex flex-col rounded-[12px] bg-[#22302B] p-[15px]">
            <img
              src={SidebarCardImg}
              alt="Residential Real Estate, Rewired."
              className="mb-2 h-[80px] w-full rounded-[10px] object-cover"
            />
            <div className="my-[5px] flex flex-row items-center justify-between gap-4">
              <div className="text-left text-[22px] font-bold leading-[135%] text-white">
                Residential Real Estate, Rewired.
              </div>
              <button
                className="flex min-h-[35px] min-w-[35px] items-center justify-center rounded-full bg-white"
                onClick={onInstantMatch}
              >
                <DirectionArrow className="size-5 text-primary" />
              </button>
            </div>
          </div>
        </div>
        <div className="mt-auto flex w-full flex-row gap-3 px-5 py-6">
          <button
            onClick={() => handleClientLoginClick(false)}
            className="h-[50px] flex-1 rounded-full border border-primary/30 bg-white text-[15px] font-semibold text-primary"
          >
            Login
          </button>
          <button
            onClick={() => handleClientLoginClick(true)}
            className="h-[50px] flex-1 rounded-full bg-[#22302B] text-[15px] font-semibold text-white"
          >
            Sign up
          </button>
        </div>
      </aside>
      <AuthModal flow={authModalFlow} open={showModal} onClose={() => setShowModal(false)} />
    </>
  )
}

export default Sidebar
